import axios from 'axios';

import { apiClient, authFirebase } from '@lift-admin/utils';
import { AlertConfigResponse, AlertsApiResponse, AlertFilters, PaginationData } from '../types';

// DMG Alert API configuration
const DMG_ALERT_API_BASE_URL = process.env.DMG_ALERT_API_URL;

// Types based on the API documentation
export interface AlertConfigCreate {
  user_id: number;
  name: string;
  description?: string;
  brand_ids: number[]; // Updated to plural to support multi-brand alerts
  alert_type_id: number;
  // Updated to nested array structure for logical operators
  // Outer array: condition groups (OR'd together)
  // Inner array: conditions within a group (connected via connect_to_next)
  threshold_configs: Array<
    Array<{
      brand_id: number;
      metric: string;
      operator: '>' | '>=' | '<' | '<=' | '==' | '!=';
      value: number;
      comparison_period?: string;
      pixel_id?: string;
      pixel_name?: string;
      event_name?: string;
      event_id?: number;
      connect_to_next?: 'AND' | 'OR'; // Logical operator connecting to next condition in group
    }>
  >;
  cadence: 'realtime' | 'hourly' | '12_hourly' | 'daily' | 'weekly' | 'monthly';
  schedule_config?: {
    time?: string;
    timezone?: string;
    days?: number[];
    day?: number;
  };
  enable_slack?: boolean;
  enable_email?: boolean;
  enable_lift_notification?: boolean;
  message_template: {
    slack: string;
    email: string;
    web: string;
    msg: string;
  };
  recipients: {
    web: any[];
    email: any[];
    slack: any[];
    users?: any[];
  };
  scheduler_job_data?: {
    time_zone?: string;
  };
  cron_info?: string;
  schedule_type?: string;
}

export interface AlertConfigUpdate {
  user_id?: number;
  name?: string;
  description?: string;
  brand_ids?: number[];
  alert_type_id?: number;
  // Updated to nested array structure for logical operators
  // Outer array: condition groups (OR'd together)
  // Inner array: conditions within a group (connected via connect_to_next)
  threshold_configs?: Array<
    Array<{
      brand_id: number;
      metric: string;
      operator: '>' | '>=' | '<' | '<=' | '==' | '!=';
      value: number;
      comparison_period?: string;
      pixel_id?: string;
      pixel_name?: string;
      event_name?: string;
      event_id?: number;
      connect_to_next?: 'AND' | 'OR'; // Logical operator connecting to next condition in group
    }>
  >;
  cadence?: 'realtime' | 'hourly' | '12_hourly' | 'daily' | 'weekly' | 'monthly';
  schedule_config?: {
    time?: string;
    timezone?: string;
    days?: number[];
    day?: number;
  };
  enable_slack?: boolean;
  enable_email?: boolean;
  enable_lift_notification?: boolean;
  message_template?: {
    slack: string;
    email: string;
    web: string;
    msg: string;
  };
  recipients?: {
    web?: any[];
    email?: any[];
    slack?: any[];
    users?: any[];
  };
  scheduler_job_data?: {
    time_zone?: string;
  };
  cron_info?: string;
  schedule_type?: string;
  is_active?: boolean;
}

// Types are now imported from '../types'

// Slack API Types
export interface SlackUser {
  id: number;
  slack_id: string;
  name: string;
  real_name: string;
  email: string;
  image_url: string;
  is_bot: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface SlackChannel {
  id: number;
  slack_id: string;
  name: string;
  is_private: boolean;
  is_archived: boolean;
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface SlackChannelWithDetails {
  id: string;
  name: string;
  is_private: number;
  is_archived: number;
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface ApiResponse<T> {
  data: T;
  pagination?: PaginationData;
}

// Teams API Types
export interface TeamUser {
  id: number;
  name: string;
  email: string;
}

class DMGAlertApiService {
  private threadTsCounter: number;

  constructor() {
    // Initialize thread_ts counter from sessionStorage or start at 100
    const stored = sessionStorage.getItem('dmg_alert_thread_ts');
    this.threadTsCounter = stored ? parseInt(stored, 10) : 100;
  }

  private getNextThreadTs(): string {
    this.threadTsCounter += 1;
    // Persist to sessionStorage
    sessionStorage.setItem('dmg_alert_thread_ts', this.threadTsCounter.toString());
    return this.threadTsCounter.toString();
  }

  // Get alerts with pagination and filters
  async getAlerts(
    page: number = 1,
    pageSize: number = 15,
    filters?: AlertFilters,
    alertType: 'my-alerts' | 'global-alerts' = 'my-alerts'
  ): Promise<{ data: AlertConfigResponse[]; pagination: PaginationData }> {
    const params: any = {
      page,
      page_size: pageSize,
    };

    // Add filter parameters if provided
    if (filters) {
      if (filters.brand_id !== undefined) {
        params.brand_id = filters.brand_id;
      }
      if (filters.alert_type_id !== undefined) {
        params.alert_type_id = filters.alert_type_id;
      }
      if (filters.is_active !== undefined) {
        params.is_active = filters.is_active;
      }
      if (filters.user_id !== undefined) {
        params.user_id = filters.user_id;
      }
    }

    // Always send is_active=1 so only active alerts are visible (my-alerts and global-alerts)
    params.is_active = 1;

    // For global alerts, explicitly remove user_id to get all alerts
    if (alertType === 'global-alerts') {
      delete params.user_id;
    }
    // For my alerts, ensure user_id is included (it should be passed in filters)

    const response = await apiClient.get<AlertsApiResponse>(
      `${DMG_ALERT_API_BASE_URL}/api/v1/alerts`,
      { params }
    );

    // Map API pagination format to frontend PaginationData format
    const apiPagination = response.data.pagination;
    const totalPages = Math.ceil(apiPagination.total / apiPagination.limit);
    // Use the page parameter we sent, or calculate from offset as fallback
    const currentPage = page || Math.floor(apiPagination.offset / apiPagination.limit) + 1;

    return {
      data: response.data.data,
      pagination: {
        currentPage,
        pageSize: apiPagination.limit,
        totalItems: apiPagination.total,
        totalPages,
      },
    };
  }

  // Get a specific alert by ID
  async getAlertById(alertId: number): Promise<AlertConfigResponse> {
    const response = await apiClient.get<AlertConfigResponse>(
      `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}`
    );
    return response.data;
  }

  // Create a new alert
  async createAlert(alertData: AlertConfigCreate): Promise<{ id: number; message: string }> {
    const response = await apiClient.post<{ id: number; message: string }>(
      `${DMG_ALERT_API_BASE_URL}/api/v1/alerts`,
      alertData
    );
    return response.data;
  }

  // Update an existing alert
  async updateAlert(
    alertId: number,
    userId: number,
    updateData: AlertConfigUpdate
  ): Promise<{ message: string }> {
    const response = await apiClient.put<{ message: string }>(
      `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}?user_id=${userId}`,
      {
        ...updateData,
        user_id: userId,
      }
    );
    return response.data;
  }

  // Activate an alert
  async activateAlert(alertId: number, userId: number): Promise<{ message: string }> {
    const response = await apiClient.patch<{ message: string }>(
      `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}/activate?user_id=${userId}`
    );
    return response.data;
  }

  // Delete an alert (soft delete - sets is_active = FALSE)
  async deleteAlert(alertId: number, userId: number): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(
      `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}`,
      {
        params: {
          user_id: userId,
        },
      }
    );
    return response.data;
  }

  // Send test alert
  async sendTestAlert(alertId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}/test`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send test alert',
      };
    }
  }

  // Subscribe to global alert
  async subscribeToGlobalAlert(alertId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/${alertId}/subscribe`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to subscribe to alert',
      };
    }
  }

  // Unsubscribe from global alert
  async unsubscribeFromGlobalAlert(
    alertId: number
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/unsubscribe-thirty-days`,
        { alert_id: alertId }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to unsubscribe from alert',
      };
    }
  }

  // Subscribe to my alert
  async subscribeToMyAlert(
    userId: number,
    alertId: number
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/subscribe`,
        { user_id: userId, alert_id: alertId }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to subscribe to alert',
      };
    }
  }

  async unsubscribeFromMyAlert(
    userId: number,
    alertId: number
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/unsubscribe-thirty-days`,
        { user_id: userId, alert_id: alertId }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to unsubscribe from alert',
      };
    }
  }

  // Get alert types
  async getAlertTypes(): Promise<{
    alert_types: Array<{
      id: number;
      name: string;
      display_name: string;
      description: string;
      data_source: string;
      created_at: string;
      updated_at: string;
      alert_query_config?: {
        properties: string[];
      };
    }>;
  }> {
    const response = await apiClient.get<{ alert_types: any[] }>(
      `${DMG_ALERT_API_BASE_URL}/api/v1/alert-types`
    );
    return response.data;
  }

  // Get EMQ-enabled brands for Step 2 selection
  async getBrands(): Promise<
    Array<{
      id: string;
      name: string;
      domain: string;
      brandId: number;
    }>
  > {
    try {
      interface EmqBrandResponseItem {
        brand_id: number;
        brand_name: string;
        is_emq?: boolean;
      }

      const response = await apiClient.get<EmqBrandResponseItem[]>(
        `${DMG_ALERT_API_BASE_URL}/api/v1/emq/emq-brands`
      );

      const brandsArray = Array.isArray(response.data) ? response.data : [];

      const brandMap = new Map<number, EmqBrandResponseItem>();

      brandsArray.forEach(brand => {
        const existing = brandMap.get(brand.brand_id);
        if (!existing || (!existing.is_emq && brand.is_emq)) {
          brandMap.set(brand.brand_id, brand);
        }
      });

      return Array.from(brandMap.values())
        .sort((a, b) => a.brand_name.localeCompare(b.brand_name))
        .map(brand => ({
          id: String(brand.brand_id),
          name: brand.brand_name,
          domain: '',
          brandId: brand.brand_id,
          isEmq: Boolean(brand.is_emq),
        }));
    } catch (error) {
      console.error('Error fetching brands:', error);
      // Return empty array as fallback
      return [];
    }
  }

  // Get brands for pacing configuration
  async getPacingBrands(): Promise<
    Array<{
      brand_id: number;
      name: string;
    }>
  > {
    try {
      const response = await apiClient.get<
        Array<{
          brand_id: number;
          name: string;
        }>
      >(`${DMG_ALERT_API_BASE_URL}/api/v1/pacing/brands`);

      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching pacing brands:', error);
      // Return empty array as fallback
      return [];
    }
  }

  // Get dimension groupings for pacing alerts
  async getDimensionGroupings(brandId: number): Promise<
    Array<{
      id: number;
      grouping_name: string;
      dimension_type: string;
      grouping_values: string;
      created_at: string;
    }>
  > {
    try {
      const response = await apiClient.get<
        Array<{
          id: number;
          grouping_name: string;
          dimension_type: string;
          grouping_values: string;
          created_at: string;
        }>
      >(`${DMG_ALERT_API_BASE_URL}/api/v1/pacing/dimension-groupings?brand_id=${brandId}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching dimension groupings:', error);
      return [];
    }
  }

  // Get standard metrics for pacing alerts
  async getStandardMetrics(): Promise<
    Array<{
      key: string;
      display_name: string;
      description: string;
    }>
  > {
    try {
      const response = await apiClient.get<
        Array<{
          key: string;
          display_name: string;
          description: string;
        }>
      >(`${DMG_ALERT_API_BASE_URL}/api/v1/pacing/standard-metrics`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching standard metrics:', error);
      return [];
    }
  }

  // Get custom metrics for pacing alerts
  async getCustomMetrics(brandId: number): Promise<
    Array<{
      id: number;
      name: string;
      latex: string;
      js_expr: string;
      metadata: string;
      created_at: string;
    }>
  > {
    try {
      const response = await apiClient.get<
        Array<{
          id: number;
          name: string;
          latex: string;
          js_expr: string;
          metadata: string;
          created_at: string;
        }>
      >(`${DMG_ALERT_API_BASE_URL}/api/v1/pacing/custom-metrics?brand_id=${brandId}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching custom metrics:', error);
      return [];
    }
  }

  // Get brand-specific metrics and dimensions for pacing alerts
  async getBrandMetricsDimensions(brandId: number): Promise<{
    metrics: string[];
    dimensions: Record<string, string[]>;
  }> {
    try {
      const response = await apiClient.get<{
        metrics: string[];
        dimensions: Record<string, string[]>;
      }>(`${DMG_ALERT_API_BASE_URL}/api/v1/pacing/brands/${brandId}/metrics-dimensions`);
      return {
        metrics: Array.isArray(response.data.metrics) ? response.data.metrics : [],
        dimensions:
          response.data.dimensions && typeof response.data.dimensions === 'object'
            ? response.data.dimensions
            : {},
      };
    } catch (error) {
      console.error('Error fetching brand metrics and dimensions:', error);
      return { metrics: [], dimensions: {} };
    }
  }

  // Get sheet configs for pacing alerts - returns valid dimension/metric combinations
  async getSheetConfigs(brandId: number): Promise<
    Array<{
      id: number;
      type: string;
      metrics: string[];
      dimensions: Record<string, string[]>;
    }>
  > {
    try {
      const response = await apiClient.get<
        Array<{
          id: number;
          type: string;
          metrics: string[];
          dimensions: Record<string, string[]>;
        }>
      >(`${DMG_ALERT_API_BASE_URL}/api/v1/pacing/brands/${brandId}/sheet-configs`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching sheet configs:', error);
      return [];
    }
  }

  // Get Facebook pixels for a specific brand
  async getPixels(brandId: number): Promise<
    Array<{
      id: number;
      brand_id: number;
      account_id: string;
      pixel_id: string;
      pixel_name: string;
      created_at: string;
      updated_at: string;
    }>
  > {
    try {
      const response = await apiClient.get(
        `${DMG_ALERT_API_BASE_URL}/api/v1/emq/facebook-pixels?brand_id=${brandId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching pixels:', error);
      // Return empty array as fallback
      return [];
    }
  }

  // Get pixel events for a specific pixel
  async getPixelEvents(pixelId: string): Promise<
    Array<{
      id: number;
      pixel_id_fk: number;
      event_name: string;
      composite_score: number | null;
      snapshot_date: string;
      created_at: string;
    }>
  > {
    try {
      const response = await apiClient.get(
        `${DMG_ALERT_API_BASE_URL}/api/v1/emq/pixel-events?pixel_id=${pixelId}&limit=100`
      );
      // Group events by name and take the first occurrence to preserve the ID
      const eventMap = new Map<
        number,
        {
          id: number;
          pixel_id_fk: number;
          event_name: string;
          composite_score: number | null;
          snapshot_date: string;
          created_at: string;
        }
      >();

      response.data.forEach((event: any) => {
        if (!eventMap.has(event.id)) {
          eventMap.set(event.id, {
            id: event.id,
            pixel_id_fk: event.pixel_id_fk,
            event_name: event.event_name,
            composite_score: event.composite_score,
            snapshot_date: event.snapshot_date,
            created_at: event.created_at,
          });
        }
      });

      return Array.from(eventMap.values());
    } catch (error) {
      console.error('Error fetching pixel events:', error);
      // Return empty array as fallback
      return [];
    }
  }

  // Get Slack users
  async getSlackUsers(): Promise<SlackUser[]> {
    try {
      const response = await apiClient.get<{ status: string; users: SlackUser[] }>(
        `${DMG_ALERT_API_BASE_URL}/api/v1/slack/users`
      );
      return response.data.users || [];
    } catch (error) {
      console.error('Error fetching Slack users:', error);
      // Return empty array as fallback
      return [];
    }
  }

  // Get Slack channels with details for a specific user
  async getSlackChannelsWithDetails(userEmail: string): Promise<SlackChannelWithDetails[]> {
    try {
      const url = `${DMG_ALERT_API_BASE_URL}/api/v1/slack/channels?email=${encodeURIComponent(userEmail)}`;

      const response = await apiClient.get(url);

      // Handle different response structures
      let channels: SlackChannelWithDetails[] = [];
      if (response.data && response.data.channels) {
        channels = response.data.channels;
      } else if (Array.isArray(response.data)) {
        channels = response.data;
      }

      return channels;
    } catch (error) {
      console.error('Error fetching Slack channels with details:', error);
      // Return empty array as fallback
      return [];
    }
  }

  // Get team users for email dropdown
  async getTeams(userId: number, brandId?: number): Promise<TeamUser[]> {
    try {
      let url = `${DMG_ALERT_API_BASE_URL}/api/v1/teams?user_id=${userId}`;
      if (brandId) {
        url += `&brand_id=${brandId}`;
      }
      const response = await apiClient.get(url);
      const users: TeamUser[] = response?.data?.team_users || [];
      return Array.isArray(users) ? users : [];
    } catch (error) {
      console.error('Error fetching teams:', error);
      return [];
    }
  }

  // Send test notification for message templates
  async sendTestMessageNotification({
    userId,
    messageTemplate,
    typeId,
    brandId,
    brandIds,
    thresholdConfigs,
    alertName,
    recipients,
  }: {
    userId: number;
    messageTemplate: {
      email_template: string;
      slack_template: string;
      web_template: string;
    };
    typeId: number;
    brandId?: number;
    brandIds?: number[];
    thresholdConfigs?: AlertConfigCreate['threshold_configs'];
    alertName?: string;
    recipients?: {
      slack?: Array<{
        user_id: number | null;
        recipient_id: string;
        recipient_type: 'slack_user' | 'slack_channel';
        recipient_value: string;
      }>;
      email?: Array<{
        user_id: number | null;
        recipient_id: number | null;
        recipient_type: 'email_user';
        recipient_value: string;
      }>;
      web?: Array<
        | {
            user_id: number;
            recipient_id: number;
            brand_id: number;
            brand_name: string;
            recipient_type: 'web_brand';
            recipient_value: {
              brand_id: number;
            };
          }
        | {
            user_id: number;
            recipient_id: number;
            recipient_type: 'web_user';
            recipient_value: {
              name: string;
              user_id: number;
              email?: string;
            };
          }
      >;
    };
  }): Promise<{ success: boolean; message: string }> {
    try {
      const payload: {
        user_id: number;
        message_template: {
          email_template: string;
          slack_template: string;
          web_template: string;
        };
        type_id: number;
        brand_id?: number;
        brand_ids?: number[];
        threshold_configs?: AlertConfigCreate['threshold_configs'];
        alert_name?: string;
        recipients?: {
          slack?: Array<{
            user_id: number | null;
            recipient_id: string;
            recipient_type: 'slack_user' | 'slack_channel';
            recipient_value: string;
          }>;
          email?: Array<{
            user_id: number | null;
            recipient_id: number | null;
            recipient_type: 'email_user';
            recipient_value: string;
          }>;
          web?: Array<
            | {
                user_id: number;
                recipient_id: number;
                brand_id: number;
                brand_name: string;
                recipient_type: 'web_brand';
                recipient_value: {
                  brand_id: number;
                };
              }
            | {
                user_id: number;
                recipient_id: number;
                recipient_type: 'web_user';
                recipient_value: {
                  name: string;
                  user_id: number;
                  email?: string;
                };
              }
          >;
        };
      } = {
        user_id: userId,
        message_template: {
          email_template: messageTemplate.email_template,
          slack_template: messageTemplate.slack_template,
          web_template: messageTemplate.web_template,
        },
        type_id: typeId,
      };

      if (alertName) {
        payload.alert_name = alertName;
      }

      if (brandId !== undefined) {
        payload.brand_id = brandId;
      }

      if (brandIds && brandIds.length > 0) {
        payload.brand_ids = brandIds;
      }

      if (thresholdConfigs && thresholdConfigs.length > 0) {
        payload.threshold_configs = thresholdConfigs;
      }

      if (recipients) {
        payload.recipients = recipients;
      }

      const response = await apiClient.post<{ success: boolean; message: string }>(
        `${DMG_ALERT_API_BASE_URL}/api/v1/alerts/test`,
        payload
      );
      return {
        success: true,
        message: response.data.message || 'Test notification sent successfully!',
      };
    } catch (error: any) {
      console.error('Error sending test notification:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send test notification',
      };
    }
  }

  // Generate message templates using Lift AI
  async generateMessageTemplates(
    userId: string | number,
    content: string,
    previousContent?: string,
    previousTemplates?: { slack?: string; email?: string; web?: string }
  ): Promise<{ slack: string; email: string; web: string }> {
    try {
      // Get Firebase auth token
      const user = authFirebase.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const requestBody: {
        content: string;
        previous_content?: string;
        previous_templates?: {
          slack?: string;
          email?: string;
          notif?: string;
        };
      } = {
        content: content,
      };

      // Include previous_content if provided
      if (previousContent) {
        requestBody.previous_content = previousContent;
      }

      // Include previous_templates if provided
      // Map 'web' to 'notif' to match API expectations
      if (previousTemplates) {
        const previousTemplatesPayload: {
          slack?: string;
          email?: string;
          notif?: string;
        } = {};

        if (previousTemplates.slack) {
          previousTemplatesPayload.slack = previousTemplates.slack;
        }
        if (previousTemplates.email) {
          previousTemplatesPayload.email = previousTemplates.email;
        }
        if (previousTemplates.web) {
          previousTemplatesPayload.notif = previousTemplates.web;
        }

        // Only include if at least one template is provided
        if (Object.keys(previousTemplatesPayload).length > 0) {
          requestBody.previous_templates = previousTemplatesPayload;
        }
      }

      const response = await apiClient.post(
        `/dmg-alerting-system/notification-templates/generate`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      console.log(response.data, content, 'response-test-notification-content');
      // Parse the response message to extract slack, email, and notif content
      const message = response.data.message || '';
      const slackMatch = message.match(/<slack>(.*?)<\/slack>/s);
      const emailMatch = message.match(/<email>(.*?)<\/email>/s);
      const notifMatch = message.match(/<notif>(.*?)<\/notif>/s);

      const slack = slackMatch ? slackMatch[1].trim() : '';
      const email = emailMatch ? emailMatch[1].trim() : '';
      const web = notifMatch ? notifMatch[1].trim() : '';

      // Check if at least one message was found
      if (!slack && !email && !web) {
        throw new Error(
          'Something went wrong. The response did not contain the expected message format. Please try again.'
        );
      }

      return {
        slack,
        email,
        web,
      };
    } catch (error: unknown) {
      console.error('Error generating message templates:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : axios.isAxiosError(error)
            ? error.response?.data?.message || error.message
            : 'Failed to generate message templates';
      throw new Error(errorMessage);
    }
  }
  /**
   * Get taxonomy brands
   */
  async getTaxonomyBrands(): Promise<
    Array<{
      id: number;
      name: string;
    }>
  > {
    try {
      const response = await apiClient.get<
        Array<{
          id: number;
          name: string;
        }>
      >(`${DMG_ALERT_API_BASE_URL}/api/v1/taxonomy/brands`);
      return response.data;
    } catch (error) {
      console.error('Error fetching taxonomy brands:', error);
      throw error;
    }
  }

  /**
   * Preview message template with sample data
   */
  async previewTemplate({
    typeId,
    brandId,
    userId,
    messageTemplate,
    thresholdConfigs,
    includeTable = true,
    alertName,
  }: {
    typeId: number;
    brandId: number;
    userId: number;
    messageTemplate: {
      email_template?: string;
      slack_template?: string;
      web_template?: string;
    };
    thresholdConfigs?: AlertConfigCreate['threshold_configs'];
    includeTable?: boolean;
    alertName?: string;
  }): Promise<{
    success: boolean;
    previews: Array<{
      brand_id: number;
      brand_name: string;
      email?: string;
      slack?: string;
      web?: string;
      email_subject?: string;
      slack_table_block?: {
        type: string;
        rows: Array<
          Array<{
            type: string;
            text: string;
          }>
        >;
        column_settings: Array<{
          align: string;
          is_wrapped: boolean;
        }>;
      };
    }>;
    message?: string;
  }> {
    try {
      const payload: {
        type_id: number;
        brand_id: number;
        user_id: number;
        message_template: {
          email_template?: string;
          slack_template?: string;
          web_template?: string;
        };
        threshold_configs: AlertConfigCreate['threshold_configs'];
        include_table: boolean;
        alert_name?: string;
      } = {
        type_id: typeId,
        brand_id: brandId,
        user_id: userId,
        message_template: messageTemplate,
        threshold_configs: thresholdConfigs || [],
        include_table: includeTable,
      };

      if (alertName) {
        payload.alert_name = alertName;
      }

      const response = await apiClient.post<{
        success: boolean;
        previews: Array<{
          brand_id: number;
          brand_name: string;
          email?: string;
          slack?: string;
          web?: string;
          email_subject?: string;
          slack_table_block?: {
            type: string;
            rows: Array<
              Array<{
                type: string;
                text: string;
              }>
            >;
            column_settings: Array<{
              align: string;
              is_wrapped: boolean;
            }>;
          };
        }>;
        message?: string;
      }>(`${DMG_ALERT_API_BASE_URL}/api/v1/alerts/preview-template`, payload);

      return {
        success: response.data.success,
        previews: response.data.previews || [],
        message: response.data.message,
      };
    } catch (error: any) {
      console.error('Error previewing template:', error);
      return {
        success: false,
        previews: [],
        message: error.response?.data?.message || 'Failed to preview template',
      };
    }
  }
}

export const dmgAlertApiService = new DMGAlertApiService();
